import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { cleanObject } from '@teko/utils';
import { getKeyAPI, normalizePageBuilderToDiscoveryParams, cookFilter } from './helpers.es.js';
import { PageTypeEnum } from './types.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const useDiscoverySearch = (discoveryService, params, location, options) => {
  const _a = params || {}, searchParams = __objRest(_a, ["sorting"]);
  const _b = options || {}, {
    transformProduct = (data2) => data2
  } = _b, swrConfigs = __objRest(_b, [
    "transformProduct"
  ]);
  const keyAPI = getKeyAPI(params, discoveryService.getBaseUrl());
  const normalizedParams = normalizePageBuilderToDiscoveryParams(searchParams);
  const { data, error } = useSWR(keyAPI, async () => {
    const { sellerIds } = params;
    let newSellerIds = sellerIds;
    if (Array.isArray(sellerIds) && typeof sellerIds[0] === "number") {
      newSellerIds = sellerIds.map((id) => id.toString());
    }
    const response = await discoveryService.search(__spreadProps(__spreadValues({}, params), {
      sellerIds: newSellerIds,
      location
    }));
    return __spreadProps(__spreadValues({}, response), {
      result: __spreadProps(__spreadValues({}, response.result), {
        products: response.result.products.map(transformProduct)
      })
    });
  }, __spreadProps(__spreadValues({}, swrConfigs), {
    revalidateOnFocus: false
  }));
  const result = data == null ? void 0 : data.result;
  return {
    result: {
      products: (result == null ? void 0 : result.products) || [],
      filters: (result == null ? void 0 : result.filter) || {},
      pagination: (data == null ? void 0 : data.pagination) || {},
      cookedFilters: cookFilter(result == null ? void 0 : result.filter, normalizedParams, result == null ? void 0 : result.masterCategories)
    },
    isLoading: !params ? false : !error && !data,
    isError: error
  };
};
const useInfiniteDiscoverySearch = (discoveryService, params, options) => {
  var _c;
  const _a = options || {}, {
    transformProduct = (data2) => data2
  } = _a, swrOptions = __objRest(_a, [
    "transformProduct"
  ]);
  const getKey = (pageIndex) => {
    const _a2 = params, rest2 = __objRest(_a2, ["page"]);
    return [rest2, pageIndex];
  };
  const convertSearchResponse = (response) => {
    var _a2, _b2;
    return {
      products: (((_a2 = response.result) == null ? void 0 : _a2.products) || []).map(transformProduct),
      suggestedKeyword: (_b2 = response.result) == null ? void 0 : _b2.suggestedKeyword,
      pagination: response.pagination
    };
  };
  const _b = useSWRInfinite(getKey, async (_params, index) => {
    const response = await discoveryService.search(__spreadProps(__spreadValues({}, params), {
      page: parseInt(params.page || "1") + index
    }));
    return convertSearchResponse(response);
  }, swrOptions), { data, isValidating, size, setSize } = _b, rest = __objRest(_b, ["data", "isValidating", "size", "setSize"]);
  const isEmpty = data && data[0].products.length === 0;
  const hasMore = !(isEmpty || data && data[data.length - 1] && data[data.length - 1].products.length < (params.itemsPerPage || 10));
  const products = data ? data.reduce((acc, curr) => {
    return [...acc, ...curr.products];
  }, []) : [];
  const cookedData = data == null ? void 0 : data[0];
  const pagination = (data == null ? void 0 : data.length) ? (_c = data[data.length - 1]) == null ? void 0 : _c.pagination : void 0;
  return __spreadValues({
    isEmpty: !!isEmpty,
    isLoading: isValidating,
    hasMore,
    size,
    setSize,
    products,
    suggestedKeyword: cookedData == null ? void 0 : cookedData.suggestedKeyword,
    isValidating,
    pagination
  }, rest);
};
const useDiscoveryProduct = (discoveryService, sku, location) => {
  const { data, error } = useSWR(!!sku ? [`${discoveryService.getBaseUrl}/product`, sku, location] : null, async (_, sku2, location2) => {
    return await discoveryService.getProduct({
      sku: sku2,
      location: location2
    });
  });
  return {
    result: {
      product: data || null
    },
    isLoading: !error && !data,
    isError: !!error
  };
};
const usePersonalizeBlocks = (discoveryService, blockParams, transformBlock) => {
  var _a, _b;
  const { pageTypeEnum, category, seller, brand, sellerCategory } = blockParams;
  const params = cleanObject(__spreadValues({}, blockParams));
  const conditionFetching = pageTypeEnum === PageTypeEnum.HOME || PageTypeEnum.NOTIFICATION || pageTypeEnum === PageTypeEnum.CATEGORY && (category || sellerCategory) || pageTypeEnum === PageTypeEnum.BRAND && brand || pageTypeEnum === PageTypeEnum.SELLER && seller;
  const { data, error } = useSWR(conditionFetching ? [`${discoveryService.getBaseUrl}/blocks`, JSON.stringify(params)] : null, async (_, paramsJson) => {
    return await discoveryService.getBlock(JSON.parse(paramsJson));
  }, {
    revalidateOnFocus: false
  });
  return {
    result: (((_a = data == null ? void 0 : data.result) == null ? void 0 : _a.blocks) || []).map(transformBlock),
    seoInfo: ((_b = data == null ? void 0 : data.result) == null ? void 0 : _b.seoInfo) || {},
    isLoading: !error && !data,
    isError: error
  };
};
const useSkus = (discoveryService, skus, location) => {
  const { data, error } = useSWR(skus ? [`${discoveryService.getBaseUrl}/products`, skus.join(","), location] : null, async (_, skus2, location2) => {
    return await discoveryService.getProductDetailBySkus({
      skus: skus2,
      location: location2
    });
  });
  return {
    result: {
      products: data || []
    },
    isLoading: !skus ? false : !error && !data,
    isError: error
  };
};

export { useDiscoveryProduct, useDiscoverySearch, useInfiniteDiscoverySearch, usePersonalizeBlocks, useSkus };
